import { Injectable } from '@angular/core';
import { SocketMessage } from 'src/app/system/utility/websocket.service';
import { MobileQueueBean, QueueStatus } from 'src/app/business/mobile-queue/mobile-queue-bean';
import { logger } from '../logging/logging.service';
import { MobileQueueService } from 'src/app/business/mobile-queue/mobile-queue.service';
import { LocationMapService } from '../../components/location-map/location-map.service';
import { combineLatest, filter, switchMap, take } from 'rxjs';
import { BranchLocation } from '../../components/location-map/location-bean';
import { DialogType, ConfirmDialogConfig } from '../../components/me-dialog/me-confirm/me-confirm.component';
import { TranslateUtilityService } from 'src/app/system/utility/translation-utility.service';
import { NavigationService } from '../../../system/utility/navigation.service';
import { Router } from '@angular/router';
import { SocketChannel } from './socket-message.bean';
import { MobileQueueUtilityService } from 'src/app/business/mobile-queue/mobile-queue-utility.service';
import { AlertController } from '@ionic/angular';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root'
})
export class QueueDispatcherService {

  private static readonly HANDLE_CHANNEL = SocketChannel.QUEUE;
  private dispatcher!: ((message: QueueMessage) => void) | null;

  private branches!: BranchLocation[];

  constructor(
    private srvUser: UserService,
    private router: Router,
    private alertController: AlertController,
    private srvMobileQueueUtility: MobileQueueUtilityService,
    private translate: TranslateUtilityService,
    private srvMobileQueue: MobileQueueService,
    private srvLocation: LocationMapService) {
    
    this.srvUser.isLoggedIn$.pipe(
      filter(isLoggedIn => isLoggedIn),
      switchMap(() => this.srvLocation.getLocations()),
      take(1)
    ).subscribe((rawBranches) => {
      this.branches = rawBranches.filter((v: BranchLocation) => v.latitude && v.longitude);
    });
  }

  public registerDispatcher(func: (message: QueueMessage) => void) {
    this.dispatcher = func;
  }

  public unregisterDispatcher() {
    this.dispatcher = null;
  }

  public handleMessage(data: SocketMessage) {
    logger.debug("QueueDispatcherService: handleMessage", data);
    let message = new QueueMessage(data.message);
    if (message.channel != QueueDispatcherService.HANDLE_CHANNEL) {
      logger.error("QueueDispatcherService: Invalid channel : ", message.channel, data.message);
      return;
    }
    if (this.dispatcher) {
      this.dispatcher(message);
    }
    else {
      this.dispatcherLocal(message);
    }
  }


  private async dispatcherLocal(message: QueueMessage) {
    logger.debug("QueueDispatcherService: dispatcherLocal", message);
    this.srvMobileQueue.getPendingQueue().subscribe((queue) => {
      logger.debug("QueueDispatcherService: getPendingQueue done ", queue);
      if (queue) {
        this.handleQueue(queue, message);
      }
      else {
        logger.error("There are no queues for this user");
      }
    });
  }
  
  private handleQueue(queue: MobileQueueBean, message: QueueMessage) {

    logger.debug("QueueDispatcherService: handleQueue", queue, message);

    switch (message.event) {
      case QueueStatus.PENDING:
        this.showPendingMessage(queue, message);
        this.srvMobileQueue.updateQueuePosition(message.queuePosition);
        break;
      case QueueStatus.ROUTED:
        this.showRoutedMessage(queue, message);
        break;
      case QueueStatus.EXPIRED:
      case QueueStatus.CLOSED:
      case QueueStatus.ABANDONED:
        // Clean up the Pending Queue as expired / close / abandoned
        this.showMessage(queue, message);
        this.srvMobileQueue.invalidPendingQueue();
        break;
      default: 
        logger.error("QueueDispatcherService: Invalid event : ", message.event, message);
        break;
    }
  }


  private showPendingMessage(queue: MobileQueueBean, message: QueueMessage) {

    let branch = this.branches.find((loc) => loc.id === queue.locationId)!;

    if (queue.queuePosition <= branch.travelReminder) {

      let confirmDialogConfig: ConfirmDialogConfig = {
        dialogType: DialogType.NORMAL,
        title: this.translate.instant('MOBILE_QUEUE.DIALOG_HEADER'),
        message: branch.message,
        confirmButtonText: this.translate.instant('MOBILE_QUEUE.DIALOG_QUEUE'),
        cancelButtonText: this.translate.instant('MOBILE_QUEUE.DIALOG_CLOSE')
      }

      let callback = () => NavigationService.goto(this.router, '/mobile-queue');

      this.showConfirmationAlert(confirmDialogConfig, callback.bind(this))

      // this.srvConfirmDialog.showDialog(confirmDialogConfig).subscribe((isConfirm) => {
      //   if (isConfirm) {
      //     NavigationService.goto(this.router, '/mobile-queue');
      //   }
      // });
    }
  }

  private showRoutedMessage(queue: MobileQueueBean, message: QueueMessage) {

    logger.debug("QueueDispatcherService: showRoutedMessage", queue, message);

    let confirmDialogConfig: ConfirmDialogConfig = {
      dialogType: DialogType.NORMAL,
      title: this.translate.instant('MOBILE_QUEUE.DIALOG_HEADER'),
      message: this.translate.instant('MOBILE_QUEUE.LABEL_ROUTED_TITLE'),
      confirmButtonText: this.translate.instant('MOBILE_QUEUE.LABEL_I_AM_HERE'),
      cancelButtonText: this.translate.instant('MOBILE_QUEUE.DIALOG_CLOSE')
    }
    let callback = () => {
      this.srvMobileQueue.markHere(queue).subscribe((isOkay: boolean) => {
        if (isOkay) {
          NavigationService.goto(this.router, '/mobile-queue');
        }
      });
    }

    this.showConfirmationAlert(confirmDialogConfig, callback.bind(this))

    // // Show Confirm Dialog and Mark I am here
    // this.srvConfirmDialog.showDialog(confirmDialogConfig).subscribe((isConfirm) => {
    //   if (isConfirm) {
    //     this.srvMobileQueue.markHere(queue).subscribe((isOkay: boolean) => {
    //       if (isOkay) {
    //         NavigationService.goto(this.router, '/mobile-queue');
    //       }
    //     });
    //   }
    // });
  }

  private showMessage(queue: MobileQueueBean, message: QueueMessage) {
    let header = this.translate.instant(this.srvMobileQueueUtility.getTitle(message.event)!);
    let instruction = this.translate.instant(this.srvMobileQueueUtility.getDefaultMessage(message.event)!);

    this.showInformationAlert(header, instruction);
    // let tipsData = this.srvTips.buildTipsData(title, [instruction]);
    // this.srvTips.openTipsDialog(tipsData);
  }


  private async showConfirmationAlert(confirmDialogConfig: ConfirmDialogConfig, callback: () => void) {

    const alert = await this.alertController.create({
      header: confirmDialogConfig.title,
      message: confirmDialogConfig.message as string,
      buttons: [
        {
          text: confirmDialogConfig.cancelButtonText!,
          role: 'cancel',
        },
        {
          text: confirmDialogConfig.confirmButtonText!,
          handler: () => callback()
        }
      ]
    });    
    await alert.present();
  }

  private async showInformationAlert(header: string, message: string) {

    const alert = await this.alertController.create({
      header: header,
      message: message,
      buttons: [
        {
          text: this.translate.instant('MOBILE_QUEUE.LABEL_OK'),
          role: 'cancel',
        }
      ]
    });    
    await alert.present();
  }

}


export class QueueMessage {
  public channel!: string;
  public userid!: string;
  public queueId!: number;
  public event!: QueueStatus
  public senderName!: string;
  public senderId!: string;
  public timestamp!: number;
  public queuePosition!: number;

  // "ch:que;pm:4867500;parentid:914;qp:1;qevent:0;senderName:server;senderId:server;timestamp:1743146649708#"  <-- When you number move
  // "ch:que;pm:007007;parentid:901;qevent:1;senderName:Huyen;senderId:huyen.hoang@mobilearth.com;timestamp:1743142858063#" <-- Routed
  // "ch:que;pm:007007;parentid:902;qevent:3;senderName:Huyen;senderId:huyen.hoang@mobilearth.com;timestamp:1743142939799#" <-- Abandoned
  // "ch:que;pm:007007;parentid:905;qevent:4;senderName:Huyen;senderId:huyen.hoang@mobilearth.com;timestamp:1743143059270#" < --Complete

  constructor(message: string) {
    // Parse Message
    let header = message.split('#')[0];
    let arrHeaders = header.split(";")
    for (let i = 0; i < arrHeaders.length; i++) {
      let hdr = arrHeaders[i];
      let detail = hdr.split(':')
      if (detail.length == 2) {
        let name = detail[0];
        let value = detail[1];
        switch (name) {
          case 'ch':
            this.channel = value;
            break;
          case 'pm':
            this.userid = value;
            break;
          case 'parentid':
            this.queueId = +value;
            break;
          case 'qevent':
            this.event = +value as QueueStatus;
            break;
          case 'senderName':
            this.senderName = value;
            break;
          case 'senderId':
            this.senderId = value;
            break;
          case 'qp':
            this.queuePosition = +value;
            break;
          case 'timestamp':
            this.timestamp = +value;
            break;
        }
      }
    }
  }
}

